import {
  Button,
  Chip,
  Icon,
  Input,
  Modal,
  SearchBar,
  Select,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  firms as firmsService,
  fronteggUsers as fronteggUsersService,
  users as usersService,
  type Firm,
} from "../../api/userVerification.service.js";
import { UserStatus, StatusFilter, type StatusFilterValue } from "./constants.js";
import {
  apiErrorMessage,
  fromUserRow,
  getStatusDisplay,
  type UserVerificationEntry,
} from "./utils.js";
import { DataTable } from "../../components/DataTable/DataTable.js";
import { PageHeader } from "../../components/PageHeader/index.js";
import { PaginationControls } from "../../components/PaginationControls/PaginationControls.js";
import { usePermissions } from "../../hooks/usePermissions.js";
import addressBookStyles from "../AddressBook/AddressBook.module.css";
import styles from "./UserVerification.module.css";

const CLIPBOARD_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

type AssignFirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  user: UserVerificationEntry | null;
  onAssigned: (userId: string, organisationId: string) => Promise<void>;
  initialSearch?: string;
};

function AssignFirmModal({
  isOpen,
  onClose,
  user,
  onAssigned,
  initialSearch,
}: AssignFirmModalProps) {
  const [searchQuery, setSearchQuery] = useState(initialSearch ?? "");
  const [results, setResults] = useState<Firm[]>([]);
  const [meta, setMeta] = useState<{ total: number; limit: number; offset: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery(initialSearch ?? "");
      setResults([]);
      setMeta(null);
      setFetchError(null);
      setIsAssigning(false);
    } else {
      setSearchQuery("");
    }
  }, [isOpen, initialSearch]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!searchQuery.trim()) {
      setResults([]);
      setMeta(null);
      setIsLoading(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        const result = await firmsService.search(searchQuery.trim(), 0);
        setResults(result.data);
        setMeta(result.meta);
      } catch {
        setFetchError("Failed to load firms. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  async function handleLoadMore() {
    if (!searchQuery.trim() || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const result = await firmsService.search(searchQuery.trim(), results.length);
      setResults(prev => [...prev, ...result.data]);
      setMeta(result.meta);
    } catch {
      setFetchError("Failed to load more firms. Please try again.");
    } finally {
      setIsLoadingMore(false);
    }
  }

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Assign Firm — ${user.firstName} ${user.lastName}`}
      size="medium"
      blurBackground
    >
      <div className={styles.modalContent}>
        <div className={styles.modalField}>
          <Input
            label="Search firms"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search by firm name..."
            fullWidth
          />
        </div>

        {isLoading && <p className={styles.firmResultsStatus}>Searching...</p>}

        {fetchError && <p className={styles.firmResultsError}>{fetchError}</p>}

        {!isLoading && !fetchError && searchQuery.trim() && results.length === 0 && (
          <p className={styles.firmResultsStatus}>No firms found.</p>
        )}

        {isAssigning && <p className={styles.firmResultsStatus}>Assigning firm...</p>}

        {!isLoading && !isAssigning && results.length > 0 && (
          <>
            <ul className={styles.firmResultsList}>
              {results.map(firm => (
                <li key={firm.id}>
                  <button
                    type="button"
                    className={styles.firmResultsItem}
                    onClick={async () => {
                      setIsAssigning(true);
                      try {
                        await onAssigned(user.id, firm.id);
                      } catch {
                        setIsAssigning(false);
                      }
                    }}
                  >
                    <span className={styles.firmResultsMeta}>{firm.name}</span>
                    <span className={styles.firmResultsMeta}>{firm.firmRef ?? "-"}</span>
                  </button>
                </li>
              ))}
              {meta !== null && results.length < meta.total && (
                <li className={styles.loadMoreContainer}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => void handleLoadMore()}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? "Loading..." : "Load more"}
                  </Button>
                </li>
              )}
            </ul>
          </>
        )}

        <div className={styles.modalActions}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function UserVerificationPage() {
  const { hasPermission } = usePermissions();
  const canAssignFirm = hasPermission("user.write_accounts");
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [roleFilter, setStatusFilter] = useState<StatusFilterValue>(StatusFilter.UNVERIFIED);
  const [users, setUsers] = useState<UserVerificationEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserVerificationEntry | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  function updateURL(updates: Record<string, string>) {
    const newParams = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value && value !== "") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    }
    setSearchParams(newParams);
  }

  useEffect(() => {
    const pageFromURL = Number(searchParams.get("page")) || 1;
    const pageSizeFromURL = Number(searchParams.get("pageSize")) || 10;
    if (pageFromURL !== currentPage) setCurrentPage(pageFromURL);
    if (pageSizeFromURL !== itemsPerPage) setItemsPerPage(pageSizeFromURL);
  }, [searchParams]);

  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length === 0 || trimmed.length >= 2) {
      const timer = setTimeout(() => setDebouncedQuery(trimmed), 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;

    async function fetchUsers() {
      setIsLoading(true);
      setFetchError(null);
      try {
        const offset = (currentPage - 1) * itemsPerPage;
        const status =
          roleFilter === StatusFilter.ALL ? "unverified,rejected,verified" : roleFilter;
        const data = await usersService.list({
          limit: itemsPerPage,
          offset,
          status,
          search: debouncedQuery || undefined,
          createdActorId: "account-bff",
        });
        if (!cancelled) {
          setUsers(data.data.map(fromUserRow));
          setTotalCount(data.meta.total);
        }
      } catch {
        if (!cancelled) {
          setFetchError("Failed to load users. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [currentPage, itemsPerPage, debouncedQuery, roleFilter]);

  function handleSearchChange(value: string) {
    setSearchQuery(value);
    if (currentPage !== 1) {
      setCurrentPage(1);
      updateURL({ page: "1" });
    }
  }

  function handleStatusFilterChange(value: string) {
    setStatusFilter(value as StatusFilterValue);
    setCurrentPage(1);
    updateURL({ page: "1" });
  }

  function handleCopyEmail(email: string) {
    navigator.clipboard
      .writeText(email)
      .then(() => {
        setToast({ message: "Email copied to clipboard", type: "success" });
      })
      .catch(() => {
        setToast({ message: "Failed to copy email", type: "error" });
      });
  }

  function handleCopyUrl(url: string) {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setToast({ message: "URL copied to clipboard", type: "success" });
      })
      .catch(() => {
        setToast({ message: "Failed to copy URL", type: "error" });
      });
  }

  function handleOpenAssignModal(user: UserVerificationEntry) {
    setSelectedUser(user);
    setIsAssignModalOpen(true);
  }

  async function handleReject(user: UserVerificationEntry) {
    try {
      await fronteggUsersService.reject(user.id);
      setUsers(currentUsers =>
        currentUsers.map(u => (u.id === user.id ? { ...u, status: UserStatus.REJECTED } : u)),
      );
      setToast({ message: "User rejected successfully", type: "success" });
    } catch (error) {
      setToast({
        message: apiErrorMessage(error, "Failed to reject user. Please try again."),
        type: "error",
      });
    }
  }

  /**
   * AccountRolePicker mount on the verify screen is deferred.
   *
   * The verify-screen row's `id` field maps to `externalId ?? platformId` (see utils.fromUserRow),
   * so the platform user UUID is not reliably available after the row is projected into
   * UserVerificationEntry. Additionally, the Frontegg-based columns do not carry an
   * organisationId. Both pieces of data are required to mount AccountRolePicker.
   *
   * Role editing for account-context users is available in UserDetail (the primary surface):
   * see services/staff-ui/src/pages/UserManagement/Users/UserDetail.tsx, account-context branch.
   */
  async function handleFirmAssigned(userId: string, organisationId: string) {
    try {
      const response = await fronteggUsersService.verify(userId, organisationId);
      setUsers(currentUsers =>
        currentUsers.map(user =>
          user.id === userId
            ? {
                ...user,
                firmName: response.firmName,
                firmRef: response.firmRef ?? "",
                status: UserStatus.VERIFIED,
              }
            : user,
        ),
      );
      setIsAssignModalOpen(false);
      setSelectedUser(null);
      setToast({ message: "Firm assigned successfully", type: "success" });
    } catch (error) {
      setToast({
        message: apiErrorMessage(error, "Failed to assign firm. Please try again."),
        type: "error",
      });
      throw error;
    }
  }

  const columns: Column<UserVerificationEntry>[] = [
    { header: "First Name", accessor: "firstName" },
    { header: "Last Name", accessor: "lastName" },
    {
      header: "Email",
      accessor: "email",
      render: value => (
        <span className={styles.emailCell}>
          {value as string}
          <button
            type="button"
            className={styles.copyButton}
            onClick={event => {
              event.stopPropagation();
              handleCopyEmail(value as string);
            }}
            aria-label={`Copy ${value as string}`}
          >
            <Icon icon={CLIPBOARD_ICON} size="small" />
          </button>
        </span>
      ),
    },
    {
      header: "Company URL",
      accessor: "companyWebsite",
      render: value => {
        const url = value as string;
        if (!url) return null;
        const displayUrl = url.length > 100 ? `${url.slice(0, 100)}…` : url;
        return (
          <span className={styles.urlCell}>
            <span className={styles.urlText} title={url}>
              {displayUrl}
            </span>
            <button
              type="button"
              className={styles.copyButton}
              onClick={event => {
                event.stopPropagation();
                handleCopyUrl(url);
              }}
              aria-label={`Copy ${url}`}
            >
              <Icon icon={CLIPBOARD_ICON} size="small" />
            </button>
          </span>
        );
      },
    },
    { header: "Submitted Firm", accessor: "submittedFirmName" },
    {
      header: "Firm Ref",
      accessor: "firmRef",
      render: (value, row) =>
        (value as string) ? (
          `${row.firmName} (${value as string})`
        ) : row.status === UserStatus.REJECTED || row.status === UserStatus.VERIFIED ? (
          "---"
        ) : (
          <span className={styles.actionButtons}>
            <span title={canAssignFirm ? undefined : "Missing user.write_accounts permission"}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={!canAssignFirm}
                onClick={event => {
                  event.stopPropagation();
                  handleOpenAssignModal(row);
                }}
              >
                Assign firm
              </Button>
            </span>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={event => {
                event.stopPropagation();
                void handleReject(row);
              }}
            >
              Reject
            </Button>
          </span>
        ),
    },
    {
      header: "Status",
      accessor: "status",
      render: value => {
        const display = getStatusDisplay(value as UserVerificationEntry["status"]);
        return (
          <Chip color={display.color} size="small">
            {display.label}
          </Chip>
        );
      },
    },
    { header: "Request Date", accessor: "requestDate" },
  ];

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + users.length;

  return (
    <div className={addressBookStyles.wrapper}>
      <PageHeader
        title="User Verification"
        subtitle="Review and verify user registration requests"
      />

      <div className={addressBookStyles.searchSection}>
        <SearchBar
          value={searchQuery}
          onChange={handleSearchChange}
          onClear={() => handleSearchChange("")}
          placeholder="Search by name or email..."
        />
      </div>

      <div className={addressBookStyles.controlsSection}>
        <div className={styles.filterRow}>
          <Select
            label="Status"
            value={roleFilter}
            onChange={event => handleStatusFilterChange(event.target.value)}
            options={[
              { value: StatusFilter.ALL, label: "All" },
              { value: StatusFilter.UNVERIFIED, label: "Unverified" },
              { value: StatusFilter.REJECTED, label: "Rejected" },
              { value: StatusFilter.VERIFIED, label: "Verified" },
            ]}
            size="small"
          />
        </div>
        <div className={addressBookStyles.pageSizeSelector}>
          <Select
            label="Items per page"
            value={itemsPerPage}
            onChange={event => {
              const newSize = Number(event.target.value);
              setItemsPerPage(newSize);
              setCurrentPage(1);
              updateURL({ pageSize: String(newSize), page: "1" });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
            ]}
            size="small"
          />
        </div>
      </div>

      <div className={addressBookStyles.contentSection}>
        {isLoading && <p className={styles.loadingMessage}>Loading users...</p>}
        {fetchError && <p className={styles.fetchError}>{fetchError}</p>}
        {!isLoading && <DataTable data={users} columns={columns} rowKey="id" />}

        {totalCount > 0 && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            totalCount={totalCount}
            itemName="users"
            onPageChange={page => {
              setCurrentPage(page);
              updateURL({ page: String(page) });
            }}
          />
        )}
      </div>

      <AssignFirmModal
        isOpen={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onAssigned={handleFirmAssigned}
        initialSearch={selectedUser?.submittedFirmName ?? ""}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={4000}
        />
      )}
    </div>
  );
}

export default UserVerificationPage;
